import { Empty } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useHistory, withRouter } from 'react-router-dom';
import styled from 'styled-components';

import TrashIcon from '../../../assets/img/TrashIcon';
import { usePageTitle } from '../../hooks';
import { DRAFT_ORDER_ACTIONS } from '../../store/actions/reducer-actions/draft-orders';
import { Status } from '../../store/models/europrisme';
import { Colors } from '../../styles/vars';
import { DraftStatus } from '../../utils/draft';
import { connect } from '../../utils/redux';
import {
  OrderDetailsPanel,
  OrderDetailsPanelTrack,
  OrderListButton,
  OrderListElement,
  OrdersListGrid,
  OrdersListWapper,
} from '../elements/AccountLists';
import BlockPlaceholder from '../elements/BlockPlaceholder';
import Button from '../elements/Button';
import Confirm from '../elements/Confirm';
import Flex from '../elements/containers/Flex';
import OrderDetails from '../elements/OrderDetails';
import PageTitle from '../elements/PageTitle';
import ErrorBoundary from '../utils/ErrorBoundary';
import Paths from './paths';

const DeleteButton = styled(Button)`
  position: relative;
  bottom: 0;
  right: 0;
  min-width: unset !important;
  padding: 3px 6px !important;
  margin-top: 6px;

  font-size: 12px;
  transform: scale(0.8);
  opacity: 0;

  transition-property: transform, opacity, background-color, color !important;
  transition-duration: 0.2s;
  transition-timing-function: ease-in-out;

  cursor: pointer;
  pointer-events: none;
`;

const PlaceholderData = Array(12)
  .fill()
  .map((_, index) => index);

const DraftOrdersList = ({
  status,
  draftOrders,
  getDraftOrders,
  disableDraftOrder,
  match,
}) => {
  usePageTitle('Mes projets de commande');

  const history = useHistory();

  const { params } = match;
  const { draftID: selected } = params;

  useEffect(() => {
    getDraftOrders();
  }, []);

  const toggleSelection = code => {
    if (selected === code) {
      history.push(Paths.DraftOrdersList({ draftID: '' }));
    } else {
      history.push(Paths.DraftOrdersList({ draftID: code }));
    }
  };

  const requestDeleteDraftOrder = (event, code) => {
    event.preventDefault();
    event.stopPropagation();

    const title = draftOrders[code]?.sDraftOrderTitle;

    Confirm({
      title: 'Supprimer le projet de commande',
      message: [
        'Êtes-vous sûr(e) de vouloir supprimer ce projet de commande ?',
        `- Projet #${code} "${title}"`,
        'Les données ne pourront pas être récupérées.',
      ],
      onConfirm: () => {
        disableDraftOrder(code);
        history.push(Paths.DraftOrdersList({ draftID: '' }));
      },
    })();
  };

  const renderList = () => {
    if (status === Status.LOADING) {
      return PlaceholderData.map(key => (
        <BlockPlaceholder key={key} height={55} />
      ));
    }

    const sortedDraftOrders = [...Object.values(draftOrders)].sort(
      (left, right) => {
        if (!left?.date || !right?.date) return 0;

        if (left.date.isBefore(right.date)) return 1;
        if (left.date.isAfter(right.date)) return -1;

        return `${right?.code}`.localeCompare(`${left?.code}`);
      },
    );

    if (sortedDraftOrders.length === 0) {
      return (
        <Flex justify="center">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Aucun projet de commande trouvé"
          />
        </Flex>
      );
    }

    const getTitleForDraftStatus = draftStatus => {
      return {
        [DraftStatus.CREATED]: 'Créé',
        [DraftStatus.SUBMITTED]: 'Soumis',
        [DraftStatus.APPROVED]: 'Approuvé',
        [DraftStatus.REJECTED]: 'Rejeté',
      }[draftStatus];
    };

    const getColorForDraftStatus = draftStatus => {
      return {
        [DraftStatus.CREATED]: Colors.white,
        [DraftStatus.SUBMITTED]: Colors.lightBlue,
        [DraftStatus.APPROVED]: Colors.lightGreen,
        [DraftStatus.REJECTED]: Colors.lightRed,
      }[draftStatus];
    };

    return sortedDraftOrders.map(
      (
        {
          code,
          sDraftOrderTitle,
          formattedDate,
          iStatus: draftStatus,
          bEnabled,
        },
        index,
      ) => (
        <OrderListElement
          key={[code, index].join('__')}
          selected={selected === code}
          hasSelection={selected}
          className="order-block"
        >
          <OrderListButton
            onClick={() => toggleSelection(code)}
            selected={selected === code}
            hasSelection={selected}
          >
            <header>
              <div className="row">
                <h3>
                  #{code}&nbsp;—&nbsp;{sDraftOrderTitle}
                </h3>

                {draftStatus && bEnabled === '1' && (
                  <div
                    className="pill relative"
                    style={{
                      backgroundColor: getColorForDraftStatus(draftStatus),
                    }}
                  >
                    {getTitleForDraftStatus(draftStatus)}
                  </div>
                )}

                {bEnabled !== '1' && (
                  <div
                    className="pill relative"
                    style={{
                      color: Colors.white,
                      backgroundColor: Colors.red,
                      fontWeight: 700,
                    }}
                  >
                    Supprimé
                  </div>
                )}
              </div>

              <div className="row">
                <h4>{formattedDate}</h4>

                {bEnabled === '1' && (
                  <DeleteButton
                    className="delete-button"
                    background="white"
                    color={Colors.red}
                    hoverBackground={Colors.red}
                    hoverColor={Colors.white}
                    onClick={event => requestDeleteDraftOrder(event, code)}
                  >
                    <TrashIcon size={14} />
                    <span>Supprimer</span>
                  </DeleteButton>
                )}
              </div>
            </header>
          </OrderListButton>
        </OrderListElement>
      ),
    );
  };

  return (
    <OrdersListWapper>
      <div className="title-row">
        <PageTitle>Mes projets de commande</PageTitle>
      </div>

      <OrdersListGrid>{renderList()}</OrdersListGrid>
      <OrderDetailsPanelTrack>
        <OrderDetailsPanel selected={selected}>
          <div className="contents">
            {selected && (
              <ErrorBoundary key={selected}>
                <OrderDetails code={selected} type="draftOrder" />
              </ErrorBoundary>
            )}
          </div>
        </OrderDetailsPanel>
      </OrderDetailsPanelTrack>
    </OrdersListWapper>
  );
};

DraftOrdersList.propTypes = {
  status: PropTypes.oneOf(Object.values(Status)).isRequired,
  draftOrders: PropTypes.objectOf(PropTypes.objectOf(PropTypes.any)),
  getDraftOrders: PropTypes.func.isRequired,
  disableDraftOrder: PropTypes.func.isRequired,

  match: PropTypes.objectOf(PropTypes.any).isRequired,
};

DraftOrdersList.defaultProps = {
  draftOrders: [],
};

export const ConnectedDraftOrdersList = connect(
  state => ({ ...state.draftOrders }),
  {
    ...DRAFT_ORDER_ACTIONS,
  },
)(withRouter(DraftOrdersList));

export default {
  path: Paths.DraftOrdersList(),
  name: 'Mes projets de commande',
  component: ConnectedDraftOrdersList,
};
